import React, { useState, useEffect, useRef } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../services/Interceptor";
import { fetchProfilePhoto } from "../store/AthleteActions";
import AutoLogin from "../pages/public/AutoLogin";
import { broadcastLogout } from "../utils/crossTabAuth";

const Navbar = () => {
  const { user } = useAuthUser() || "";
  const logOut = useSignOut();
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const { profilePhoto } = useSelector((state) => state.athletes);
  const hasSubscription = useSelector((state) => state.user.hasSubscription);

  const [profilePicture, setProfilePicture] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(fetchProfilePhoto(apiInstance, user?.id));
    }
  }, [dispatch, apiInstance, user?.id]); // eslint-disable-line

  useEffect(() => {
    if (profilePhoto) {
      setProfilePicture(profilePhoto);
    }
  }, [profilePhoto]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const style = {
    active: "text-lg my-auto text-white font-semibold",
    inactive: "text-lg text-white my-auto hover:text-white hover:font-semibold",
    isPending: "",
  };

  const signOut = () => {
    logOut();
    broadcastLogout();
    setProfilePicture(null);
    window.location.href = "/sign-in";
  };

  return (
    <>
      <nav
        className={`transition-all duration-300 py-4 px-8
         ${isScrolled ? "fixed top-0 left-0 " : "absolute top-0 left-0 bg-transparent "}
           relative top-0 left-0 w-full z-40
           sm:relative md:relative lg:relative xl:relative  
           `}
      >
        <div
          className={`flex justify-between items-center ${
            user ? "w-full" : "w-full max-w-7xl mx-auto"
          }`}
        >
          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-gray-900"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <FontAwesomeIcon
                icon={faTimes}
                size="lg"
                className="text-white"
              />
            ) : (
              <FontAwesomeIcon icon={faBars} size="lg" className="text-white" />
            )}
          </button>

          {/* Logo */}
          <div className="flex-shrink-0">
            <NavLink to="/">
              <img
                className=" h-10 lg:h-14"
                alt="Trackit"
                src="/trackitt-logo.png"
              />
            </NavLink>
          </div>

          {/* Profile Photo */}
          <div className="lg:hidden">
            {user && (
              <div
                className="relative inline-block text-left"
                ref={dropdownRef}
              >
                {/* Profile Picture */}
                <img
                  src={profilePicture?.fileUrl || "/default-profile-img.png"}
                  alt="Profile"
                  onClick={toggleDropdown}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-500"
                />

                {/* Dropdown */}
                {isOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <ul className="py-2 text-sm text-gray-700">
                      <AutoLogin navButton variant="mobile-dropdown" />
                      <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                        {!user?.role ? (
                          <></>
                        ) : (
                          <NavLink
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              signOut();
                            }}
                            className="text-gray-700 text-lg"
                          >
                            Sign Out
                          </NavLink>
                        )}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            <div className="ml-10 flex items-center space-x-12">
              {user && user.role && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={`/${user.role}/dashboard`}
                >
                  Dashboard
                </NavLink>
              )}
              <NavLink
                className={({ isActive, isPending }) =>
                  isPending
                    ? style.isPending
                    : isActive
                      ? style.active
                      : style.inactive
                }
                to={"/"}
              >
                Home
              </NavLink>
              <NavLink
                className={({ isActive, isPending }) =>
                  isPending
                    ? style.isPending
                    : isActive
                      ? style.active
                      : style.inactive
                }
                to={"/events"}
              >
                Events
              </NavLink>
              {!user?.role && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={"/Contact"}
                >
                  Contact Us
                </NavLink>
              )}
              {hasSubscription ? (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={"/leaderboard"}
                >
                  Leaderboard
                </NavLink>
              ) : null}
              {!user?.role ? (
                <>
                  <NavLink
                    className="group text-white bg-red-500 font-medium px-8 py-2 rounded-full text-lg hover:bg-red-500/70 transition"
                    to={"/sign-in"}
                  >
                    Sign In{" "}
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </NavLink>
                </>
              ) : (
                <>
                  <AutoLogin navButton />
                  <NavLink
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      signOut();
                    }}
                    className={({ isActive, isPending }) =>
                      `${
                        isPending
                          ? style.isPending
                          : isActive
                            ? style.active
                            : style.inactive
                      } group text-white bg-red-500 font-medium px-8 py-2 rounded-full text-lg hover:bg-red-500/70 transition`
                    }
                  >
                    Sign Out{" "}
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>

        {/* Background Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 top-16"
            onClick={toggleMobileMenu}
          ></div>
        )}

        {/* Mobile Menu */}
        <div
          className={`fixed top-16 inset-y-0 left-0 bg-black w-72 transform transition-transform duration-300 ease-in-out z-50 md:block ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <button
            className="absolute top-4 right-4 text-gray-700 hover:text-gray-900"
            onClick={toggleMobileMenu}
          ></button>
          <nav className="flex flex-col space-y-4 py-6 px-4">
            <div className="py-4 px-0">
              {user && user.role && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                  }
                  to={`/${user.role}/dashboard`}
                >
                  Dashboard
                </NavLink>
              )}
              <NavLink
                className={({ isActive, isPending }) =>
                  `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                }
                to={"/"}
              >
                Home
              </NavLink>
              <NavLink
                className={({ isActive, isPending }) =>
                  `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                }
                to={"/events"}
              >
                Events
              </NavLink>

              {hasSubscription && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={"/leaderboard"}
                >
                  Leaderboard
                </NavLink>
              )}

              {!user?.role ? (
                <>
                  <NavLink
                    className={({ isActive, isPending }) =>
                      `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                    }
                    to={"/Contact"}
                  >
                    Contact Us
                  </NavLink>
                  <NavLink
                    className={({ isActive, isPending }) =>
                      `${isPending ? style.isPending : isActive ? style.active : style.inactive} group text-white bg-red-500 font-medium px-8 py-3 rounded-full text-lg hover:bg-red-500/70 transition`
                    }
                    to={"/sign-in"}
                  >
                    Sign In
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </NavLink>
                </>
              ) : (
                <>
                  <AutoLogin navButton variant="mobile-menu" />
                  <NavLink
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      signOut();
                    }}
                    className={({ isActive, isPending }) =>
                      `${isPending ? style.isPending : isActive ? style.active : style.inactive} pt-8 block`
                    }
                  >
                    Sign Out
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>
      </nav>
    </>
  );
};
export default Navbar;
